import { ChangeEventHandler, MutableRefObject, useEffect, useId, useRef, useState } from 'react'
import { FieldError } from 'react-hook-form'
import { CSSObject } from '@emotion/serialize'
import ClearIcon from '@mui/icons-material/Clear'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'
import { GlobalStyles, IconButton, InputAdornment, styled, SxProps, TextField } from '@mui/material'
import { isEmpty } from 'lodash'

import { DefaultElementHandlers } from 'common/interfaces'
import InputLabel from 'components/InputLabel'

import { MapAddressState as MapAddressInitialState } from '../stateInterface'
import useGoogleMapsApi from '../useGoogleMapsApi'
import usePacContainerExpanded from '../usePacContainerExpanded'

type MapAddressState = MapAddressInitialState & {
  addressComponents?: google.maps.GeocoderAddressComponent[]
}

export interface MapAutocompleteFieldProps {
  label?: string
  placeholder?: string
  value: string
  onChange: (value: MapAddressState) => void
  onFocus?: DefaultElementHandlers['onFocus']
  map: google.maps.Map
  error?: FieldError
  sx?: SxProps
  pacSx?: CSSObject
  required?: boolean
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.shape.borderRadius,
}))

export default function MapAutocompleteField({
  label,
  placeholder,
  value,
  onChange,
  onFocus,
  error,
  map,
  sx,
  pacSx,
  required,
}: MapAutocompleteFieldProps): JSX.Element {
  const gmaps = useGoogleMapsApi()
  const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null)
  const [internalValue, setInternalValue] = useState<string>(value ?? '')
  const { isExpanded: isPacExpanded, pacListboxId } = usePacContainerExpanded(inputRef)
  const reactId = useId()
  const helperTextId = `map-address-helper-${reactId.replace(/:/g, '')}`

  useEffect(() => {
    if (inputRef.current !== null) {
      const autocomplete = new gmaps.places.Autocomplete(inputRef.current)
      map !== undefined && autocomplete.bindTo('bounds', map)
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        if (place.geometry?.location === undefined) {
          // User entered the name of a Place that was not suggested and
          // pressed the Enter key, or the Place Details request failed.
          return
        }

        onChange?.({
          addressComponents: place.address_components,
          formattedAddress: place.formatted_address,
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          },
        })
      })
    }
  }, [])

  useEffect(() => {
    setInternalValue(value ?? '')
  }, [value])

  const onInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target
    if (isEmpty(value)) {
      onInputClear()

      return
    }

    setInternalValue(value)
  }

  const onInputClear = (): void => {
    onChange?.({})
    setInternalValue('')
  }

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          '.pac-container': {
            zIndex: theme.zIndex.modal,
            ...pacSx,
          },
        })}
      />
      <StyledTextField
        label={<InputLabel label={label} />}
        sx={sx}
        value={internalValue}
        onChange={onInputChange}
        onFocus={onFocus}
        error={error !== undefined}
        helperText={error?.message}
        placeholder={placeholder}
        inputRef={inputRef}
        margin="normal"
        required={required}
        inputProps={{
          role: 'combobox',
          'aria-autocomplete': 'list' as const,
          'aria-expanded': isPacExpanded,
          'aria-controls': isPacExpanded ? pacListboxId : undefined,
          'aria-invalid': error !== undefined,
          'aria-describedby': error !== undefined ? helperTextId : undefined,
        }}
        FormHelperTextProps={{
          id: helperTextId,
          role: error !== undefined ? 'alert' : undefined,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RoomOutlinedIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment:
            internalValue !== '' ? (
              <InputAdornment position="end">
                <IconButton onClick={onInputClear} aria-label="Clear location">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
      />
    </>
  )
}
